import React, { useEffect, useState, createContext, useContext } from 'react';
import { auth, googleProvider, db, dbRealtime, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from '../firebase';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { setDoc, doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { ref, set as dbSet, remove as dbRemove, get as dbGet } from 'firebase/database';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userDoc, setUserDoc] = useState(null);
    const [userDocLoading, setUserDocLoading] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            setUser(firebaseUser);
            if (firebaseUser) {
                setUserDocLoading(true);
                const docRef = doc(db, 'users', firebaseUser.email);
                const snap = await getDoc(docRef);
                let userData = snap.exists() ? snap.data() : null;

                // Update UID in Firestore if not present or changed
                if (userData && (!userData.uid || userData.uid !== firebaseUser.uid)) {
                    try {
                        await updateDoc(docRef, { uid: firebaseUser.uid });
                        userData = { ...userData, uid: firebaseUser.uid };
                    } catch (error) {
                        console.error('Error updating UID in Firestore:', error);
                    }
                }

                setUserDoc(userData);

                // Sync admin status to Realtime Database
                if (userData?.admin && firebaseUser.uid) {
                    try {
                        await dbSet(ref(dbRealtime, `admins/${firebaseUser.uid}`), true);
                        await syncAllAdminsToRealtime();
                    } catch (error) {
                        if (error.code !== 'PERMISSION_DENIED') {
                            console.error('Error syncing admin status:', error);
                        }
                    }
                } else if (userData && !userData.admin && firebaseUser.uid) {
                    try {
                        await dbRemove(ref(dbRealtime, `admins/${firebaseUser.uid}`));
                    } catch (error) {
                        if (error.code !== 'PERMISSION_DENIED') {
                            console.error('Error removing admin status:', error);
                        }
                    }
                }

                setUserDocLoading(false);
                updateLastLogin(firebaseUser.email);
            } else {
                setUserDoc(null);
            }
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    const updateLastLogin = async (email) => {
        try {
            const userRef = doc(db, 'users', email);
            await updateDoc(userRef, {
                lastLogin: new Date().toISOString(),
            });
        } catch (error) {
            console.error('Error updating last login:', error);
        }
    };

    const syncAllAdminsToRealtime = async () => {
        try {
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const admins = {};

            usersSnapshot.forEach((docSnap) => {
                const userData = docSnap.data();
                if (userData.admin && userData.uid) {
                    admins[userData.uid] = true;
                }
            });

            const adminsRef = ref(dbRealtime, 'admins');
            const currentAdminsSnapshot = await dbGet(adminsRef);
            const currentAdmins = currentAdminsSnapshot.exists() ? currentAdminsSnapshot.val() : {};

            // Add new admins
            for (const uid in admins) {
                if (!currentAdmins[uid]) {
                    try {
                        await dbSet(ref(dbRealtime, `admins/${uid}`), true);
                    } catch (error) {
                        if (error.code !== 'PERMISSION_DENIED') {
                            console.warn(`Cannot sync admin ${uid}: ${error.message}`);
                        }
                    }
                }
            }

            // Remove admins no longer in Firestore
            for (const uid in currentAdmins) {
                if (!admins[uid]) {
                    try {
                        await dbRemove(ref(dbRealtime, `admins/${uid}`));
                    } catch (error) {
                        if (error.code !== 'PERMISSION_DENIED') {
                            console.warn(`Cannot remove admin ${uid}: ${error.message}`);
                        }
                    }
                }
            }
        } catch (error) {
            if (error.code !== 'PERMISSION_DENIED') {
                console.error('Error syncing admins to Realtime Database:', error);
            }
        }
    };

    const login = async () => {
        try {
            await signInWithPopup(auth, googleProvider);
        } catch (error) {
            if (error.code === 'auth/popup-closed-by-user') {
                console.log('Google sign-in popup was closed by user');
                return;
            }
            console.error('Google sign-in error:', error);
        }
    };

    const registerWithPassword = async (email, password) => {
        try {
            const result = await createUserWithEmailAndPassword(auth, email, password);
            return { success: true, user: result.user };
        } catch (error) {
            console.error('Error registering with password:', error);
            return { success: false, error: error.message };
        }
    };

    const loginWithPassword = async (email, password) => {
        try {
            const result = await signInWithEmailAndPassword(auth, email, password);
            return { success: true, user: result.user };
        } catch (error) {
            console.error('Error signing in with password:', error);
            return { success: false, error: error.message };
        }
    };

    const resetPassword = async (email) => {
        try {
            await sendPasswordResetEmail(auth, email);
            return { success: true };
        } catch (error) {
            console.error('Error sending password reset email:', error);
            return { success: false, error: error.message };
        }
    };

    const logout = async () => {
        await signOut(auth);
    };

    return (
        <AuthContext.Provider value={{ user, login, registerWithPassword, loginWithPassword, resetPassword, logout, loading, userDoc, userDocLoading }}>
            {children}
        </AuthContext.Provider>
    );
}
